// src/components/programs/ProgramForm/Step1Sections/ProductsPanel/ProductCard.jsx

import React, { useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    alpha,
    useTheme,
    Stack,
} from "@mui/material";
import {
    Category as CategoryIcon,
    // AttachMoney as PriceIcon,
    DragIndicator as DragIcon,
    Business as SupplierIcon,
} from "@mui/icons-material";
import { createProductDragData, setDragData } from "../utils/DragDropContext";

const ProductCard = ({
    product,
    isArabic,
    activeLang,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isDragging, setIsDragging] = useState(false);

    // Get title
    const title = product.title?.[activeLang] || product.title?.en || product.title || '';

    // Get category name
    const categoryName = product.category?.name?.[activeLang] ||
        product.category?.name?.en ||
        product.category?.name || '';
    const locationsName = product.locations?.[activeLang] ||
        product.locations?.en ||
        product.locations?.name || '';

    // Get pricing code
    const pricingCode = product.product_snapshot?.pricing_unit || product.pricing_code ||
        product.pricing_unit?.code || '';

    // Get suppliers
    const suppliers = product.suppliers || [];
    const mainSupplier = suppliers.length > 0
        ? (suppliers[0].name?.[activeLang] || suppliers[0].name?.en || suppliers[0].name || '')
        : '';

    /**
     * Handle drag start
     */
    const handleDragStart = (e) => {
        setIsDragging(true);
        const dragData = createProductDragData(product);
        setDragData(e.dataTransfer, dragData);
        e.dataTransfer.effectAllowed = 'copy';
    };

    /**
     * Handle drag end
     */
    const handleDragEnd = () => {
        setIsDragging(false);
    };

    return (
        <Card
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            elevation={isDragging ? 6 : 1}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'grab',
                opacity: isDragging ? 0.5 : 1,
                transform: isDragging ? 'scale(1.02)' : 'none',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'visible',
                borderRadius: 2,
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                    '& .drag-hint': {
                        opacity: 1,
                    },
                },
                '&:active': {
                    cursor: 'grabbing',
                },
                border: `1.5px solid ${isDark
                    ? alpha('#FFFFFF', 0.08)
                    : alpha('#000000', 0.06)
                    }`,
            }}
        >
            {/* Drag Indicator */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 6,
                    right: isArabic ? 'auto' : 6,
                    left: isArabic ? 6 : 'auto',
                    opacity: 0.2,
                    transition: 'opacity 0.2s',
                    '.MuiCard-root:hover &': {
                        opacity: 0.5,
                    },
                }}
            >
                <DragIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 1.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Title - Compact */}
                <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                        fontSize: '0.875rem',
                        lineHeight: 1.3,
                        minHeight: 34,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1.5,
                        pr: 2.5, // Space for drag icon
                    }}
                >
                    {title}
                </Typography>

                {/* Info Tags - Compact in 2 rows max */}
                <Stack spacing={0.75} direction="row" sx={{ mb: 1.5, alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    {/* Row 1: Category + Pricing */}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {categoryName && (
                            <Chip
                                icon={<CategoryIcon style={{ fontSize: 14 }} />}
                                label={categoryName}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main',
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                    '& .MuiChip-icon': {
                                        fontSize: 14,
                                        ml: 0.5,
                                    },
                                }}
                            />
                        )}

                        {pricingCode && (
                            <Chip
                                label={pricingCode.replace('_', ' ').toUpperCase()}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.3px',
                                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                    color: 'success.main',
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                                }}
                            />
                        )}
                        {locationsName && (
                            <Chip
                                label={locationsName}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main',
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                    '& .MuiChip-icon': {
                                        fontSize: 14,
                                        ml: 0.5,
                                    },
                                }}
                            />
                        )}
                    </Stack>

                    {/* Row 2: Supplier */}
                    {mainSupplier && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                                icon={<SupplierIcon style={{ fontSize: 14 }} />}
                                label={mainSupplier}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                    color: 'info.main',
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                                    maxWidth: suppliers.length > 1 ? 'calc(100% - 30px)' : '100%',
                                    '& .MuiChip-icon': {
                                        fontSize: 14,
                                        ml: 0.5,
                                    },
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            />
                            {suppliers.length > 1 && (
                                <Chip
                                    label={`+${suppliers.length - 1}`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        minWidth: 24,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        bgcolor: alpha(theme.palette.info.main, 0.08),
                                        color: 'info.main',
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                                    }}
                                />
                            )}
                            {/* Compact Price Box */}
                            {/* <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 0.5,
                                    borderRadius: 1.5,
                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 20,
                                        height: 20,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(theme.palette.success.main, 0.15),
                                    }}
                                >
                                    <Typography
                                        color="success.main"
                                        display="block">
                                        ฿
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={800}
                                        color="success.main"
                                        sx={{ fontSize: '0.95rem', lineHeight: 1 }}
                                    >
                                        {product.price}
                                    </Typography>
                                </Box>
                            </Box> */}
                            <Chip
                                icon={<Typography color="success.main" display="block"> ฿</Typography>}
                                label={product?.price}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                    color: 'success.main',
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                                    maxWidth: suppliers.length > 1 ? 'calc(100% - 30px)' : '100%',
                                    '& .MuiChip-icon': {
                                        fontSize: 14,
                                        ml: 0.5,
                                    },
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
            </CardContent>

            {/* Compact Drag Hint - Overlay */}
            <Box
                className="drag-hint"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.5),
                    backdropFilter: 'blur(1px)',
                    borderRadius: 2,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    pointerEvents: 'none',
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: '#FFFFFF',
                        color: theme.palette.primary.main,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        boxShadow: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <DragIcon sx={{ fontSize: 16 }} />
                    {isArabic ? 'اسحب إلى اليوم' : 'Drag to Day'}
                </Box>
            </Box>
        </Card>
    );
};

export default ProductCard;
